"use client";

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAuthError, selectAuthStatus } from "store";
import { resetPassword } from "store";
import Logo from "./logo";
import EveLogo from "icons/src/everest-logoblack.svg";
import { Button } from "ui/components/button";
import { Input } from "ui/components/input";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export function ResetPassword() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [token, setToken] = useState(null);
    const [isValidToken, setIsValidToken] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [passwordError, setPasswordError] = useState("");
    const authStatus = useSelector(selectAuthStatus);
    const authError = useSelector(selectAuthError);

    useEffect(() => {
        const tokenFromUrl = searchParams.get("token");
        if (!tokenFromUrl) {
            setIsValidToken(false);
            setIsLoading(false);
            return;
        }

        setToken(tokenFromUrl);
        setIsLoading(false);
    }, [searchParams]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (password.length < 6) {
            setPasswordError("Password must be at least 6 characters");
            return;
        }

        if (password !== confirmPassword) {
            setPasswordError("Passwords don't match");
            return;
        }

        setPasswordError("");
        dispatch(resetPassword({ token, password })).then((result) => {
            if (result.type === 'auth/resetPassword/fulfilled') {
                toast.success("password Reset Successfully");
                navigate("/login");
            }
        });
    };
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    if (!isValidToken) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="max-w-md w-full mx-4">
                    <div className="bg-white border rounded-lg p-8 text-center">
                        <div className="mb-4">
                            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto">
                                <span className="text-white text-4xl">!</span>
                            </div>
                        </div>
                        <h1 className="text-2xl font-bold mb-4">Invalid Reset Link</h1>
                        <p className="text-gray-600 mb-6">
                            The password reset link is invalid or has expired. Please request
                            a new password reset link.
                        </p>
                        <Button
                            onClick={() => navigate("/login")}
                            className="w-full"
                        >
                            Back to Login
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background md:p-16 gap-x-6 flex justify-center items-center relative">
            {/* Content wrapper */}
            <div className="w-full flex items-top justify-center">
                {/* Sign-in form container */}
                <div className="max-w-sm px-6 py-16 md:p-0 w-full ">
                    {/* Logo and header section */}
                    <div className="space-y-6 mb-10 items-center flex flex-col">
                        <img src={EveLogo} alt="Logo" className="w-55" />
                    </div>
                    {/* Input fields section */}
                    <div className="space-y-4 mb-4">
                        <div className="space-y-2">
                            <div>
                                <Input
                                    id="password"
                                    placeholder="New Password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleSubmit(e);
                                        }
                                    }}
                                />
                            </div>
                            <div>
                                <Input
                                    id="confirmPassword"
                                    placeholder="Confirm Password"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleSubmit(e);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    {/* Error messages */}
                    <div className="flex flex-col space-y-6">
                        {(authError || passwordError) && (
                            <p className="text-red-500 text-sm text-center mb-4">
                                {passwordError || authError}
                            </p>
                        )}
                        <Button
                            className="w-full"
                            onClick={handleSubmit}
                            disabled={authStatus === "loading"}
                        >
                            Reset
                        </Button>
                    </div>
                    {/* Centered Powered by section */}
                    <div
                        className="w-full flex justify-center items-center gap-2 mt-8 opacity-80"
                    >
                        <span className="text-ms text-muted-foreground">Powered by</span>
                        <Logo width={80} height={25} />
                    </div>
                </div>
            </div>
        </div>
    );
}
